import React, { useEffect, useState } from 'react';

import DateTimePicker from '../DateTimePicker';
import { parseDate, formatDateStr } from '../tablemaint/DateField';
import type { BulkEditMeta } from '../TanTable';

/**
 * An in-place editor for one grid cell, so a single field can be changed without
 * opening the record.
 *
 * Two behaviours it has to get right, both invisible when they work:
 *
 *   ROW CLICK. Tablemaint rows open the edit drawer on click, so a left click that
 *   lands on this control must not bubble — otherwise every attempt to type in a
 *   cell opens the drawer over the top of it. RIGHT clicks are let through
 *   deliberately: that is how TanTable starts its drag-select, and a column you can
 *   edit one cell at a time should still be draggable across a range.
 *
 *   TYPING. Free-typed values commit on blur rather than per keystroke, so a
 *   character doesn't rebuild the whole column array. Dropdowns commit immediately —
 *   there is no intermediate state worth holding.
 *
 * Nothing here writes to the server. Edits are staged in the page and go out
 * together when the user saves.
 */

export interface EditableCellProps {
  /** The value in force — the staged edit if there is one, else the stored value. */
  value: unknown;
  /** Widget to render, from the column's bulk-edit meta. */
  meta: BulkEditMeta;
  /** True when this cell carries an unsaved edit (drives the highlight). */
  edited: boolean;
  disabled?: boolean;
  onCommit: (value: unknown) => void;
}

const BASE =
  'w-full min-w-0 bg-transparent px-1 py-0.5 text-xs rounded border ' +
  'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

/** Resting state announces "this is editable" without shouting: a faint dashed
 *  outline that firms up on hover. An edited cell goes amber and stays there until
 *  the batch is saved or discarded. */
const restingClass = (edited: boolean): string =>
  edited
    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-500'
    : 'border-dashed border-gray-300 hover:border-gray-500 dark:border-gray-600 dark:hover:border-gray-400';

/** A stored date normalised to ISO 'YYYY-MM-DD' for the picker. The engine hands
 *  most dates back that way already; the zero-date sentinel and blanks become ''. */
const toDateInput = (v: unknown): string => {
  const s = String(v ?? '').trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return m[1] === '0000' ? '' : `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return '';
};

export const EditableCell: React.FC<EditableCellProps> = ({
  value, meta, edited, disabled, onCommit,
}) => {
  // Left click stays in the cell; right click bubbles so drag-select still works.
  const swallowLeftClick = {
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => { if (e.button !== 2) e.stopPropagation(); },
  };
  const className = `${BASE} ${restingClass(edited)}`;

  if (meta.type === 'dropdown') {
    const current = String(value ?? '');
    const known = (meta.options ?? []).some((o) => o.value === current);
    return (
      <select
        {...swallowLeftClick}
        value={current}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.value)}
        className={className}
      >
        {/* A stored value outside the option set stays visible instead of silently
            snapping to the first option. */}
        {!known && <option value={current}>{current || '—'}</option>}
        {(meta.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (meta.type === 'date') {
    // The app's own picker, not <input type="date">: it carries the numeric-entry
    // shortcuts people use everywhere else (type 831, get Aug 31). Portaled to the
    // body because a grid cell has no dialog to escape from and the table's own
    // overflow would clip the calendar.
    return (
      <div {...swallowLeftClick} className={edited ? 'rounded bg-amber-50 dark:bg-amber-900/30' : ''}>
        <DateTimePicker
          mode="date"
          selectionType="single"
          inputVariant="compact"
          labelType="none"
          autoApply
          usePortal
          autoFlip
          value={parseDate(toDateInput(value))}
          disabled={disabled}
          onChange={(v) => onCommit(formatDateStr(v instanceof Date ? v : null))}
        />
      </div>
    );
  }

  return (
    <FreeInput
      kind={meta.type === 'number' ? 'number' : 'text'}
      incoming={String(value ?? '')}
      className={className}
      disabled={disabled}
      handlers={swallowLeftClick}
      onCommit={onCommit}
    />
  );
};

/** Keeps keystrokes local and commits on blur (or Enter). `incoming` resyncs when
 *  the value changes underneath — a drag-select over this cell, a discard, a
 *  refresh. */
const FreeInput: React.FC<{
  kind: 'number' | 'text';
  incoming: string;
  className: string;
  disabled?: boolean;
  handlers: Record<string, unknown>;
  onCommit: (value: unknown) => void;
}> = ({ kind, incoming, className, disabled, handlers, onCommit }) => {
  const [val, setVal] = useState(incoming);
  useEffect(() => { setVal(incoming); }, [incoming]);
  return (
    <input
      {...handlers}
      type={kind}
      value={val}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== incoming) onCommit(val); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setVal(incoming); e.currentTarget.blur(); }
      }}
      className={className}
    />
  );
};

export default EditableCell;
